import { Helmet } from 'react-helmet-async'
import { motion } from 'framer-motion'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { 
  Shield, 
  Code, 
  ArrowRight, 
  CheckCircle,
  Zap,
  Lock,
  Search,
  Users,
  Smartphone,
  FileCode,
  AlertTriangle,
  CheckCircle2
} from 'lucide-react'
import { services } from '../lib/data'

const ServicesPage = () => {
  const [activeTab, setActiveTab] = useState<'security' | 'development'>('security')
  
  const securityServices = services.filter(s => s.category === 'security')
  const developmentServices = services.filter(s => s.category === 'development')

  const getIcon = (iconName: string) => {
    const iconMap: Record<string, any> = {
      Shield, Code, Zap, Lock, Search, Users, Smartphone, FileCode, AlertTriangle, CheckCircle2
    }
    return iconMap[iconName] || Shield
  }

  return (
    <>
      <Helmet>
        <title>Services - DevSecRoot</title>
        <meta name="description" content="Comprehensive security and development services including penetration testing, secure development, microservices architecture, and more." />
        <link rel="canonical" href="https://devsecroot.com/services" />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-32 pb-16">
        <div className="container-max text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold text-dsr-text mb-6">
              Our Services
            </h1>
            <p className="text-xl text-dsr-text/70 max-w-2xl mx-auto">
              Comprehensive security and development services to protect and scale your business. 
              From penetration testing to microservices architecture, we've got you covered.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Tabs */}
      <section className="section-padding">
        <div className="container-max">
          <div className="flex justify-center mb-12">
            <div className="bg-dsr-card backdrop-blur-md border border-white/10 rounded-2xl p-2">
              <div className="flex space-x-2">
                <button
                  onClick={() => setActiveTab('security')}
                  className={`px-8 py-3 rounded-xl font-medium transition-all duration-200 ${
                    activeTab === 'security'
                      ? 'bg-gradient-to-r from-red-500 to-pink-500 text-white shadow-lg'
                      : 'text-dsr-text/70 hover:text-dsr-text'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <Shield className="w-5 h-5" />
                    <span>Security Services</span>
                  </div>
                </button>
                <button
                  onClick={() => setActiveTab('development')}
                  className={`px-8 py-3 rounded-xl font-medium transition-all duration-200 ${
                    activeTab === 'development'
                      ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white shadow-lg'
                      : 'text-dsr-text/70 hover:text-dsr-text'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <Code className="w-5 h-5" />
                    <span>Development Services</span>
                  </div>
                </button>
              </div>
            </div>
          </div>

          {/* Services Grid */}
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {(activeTab === 'security' ? securityServices : developmentServices).map((service, index) => {
              const IconComponent = getIcon(service.icon)
              return (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="glass-card-hover p-6 h-full flex flex-col"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-dsr-accent to-cyan-500 rounded-xl flex items-center justify-center mb-4">
                    <IconComponent className="w-6 h-6 text-black" />
                  </div>
                  
                  <h3 className="text-xl font-semibold text-dsr-text mb-3">{service.title}</h3>
                  <p className="text-dsr-text/70 mb-4 flex-grow">{service.description}</p>
                  
                  <div className="space-y-3 mb-6">
                    {service.features.slice(0, 3).map((feature, idx) => (
                      <div key={idx} className="flex items-center space-x-2">
                        <CheckCircle className="w-4 h-4 text-dsr-accent flex-shrink-0" />
                        <span className="text-sm text-dsr-text/80">{feature}</span>
                      </div>
                    ))}
                  </div>
                  
                  <Link 
                    to={service.link}
                    className="inline-flex items-center space-x-2 text-dsr-accent hover:text-cyan-400 transition-colors mt-auto"
                  >
                    <span>Learn more</span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </motion.div>
              )
            })}
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding bg-dsr-card/30">
        <div className="container-max text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-dsr-text mb-6">
              Ready to Get Started?
            </h2>
            <p className="text-xl text-dsr-text/70 mb-8 max-w-2xl mx-auto">
              Let's discuss your project requirements and how we can help you achieve your security and development goals.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-primary text-lg px-8 py-4">
                Start Your Project
              </Link>
              <Link to="/contact" className="btn-secondary text-lg px-8 py-4">
                Schedule a Consultation
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  )
}

export default ServicesPage
